import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRevalidator } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { format } from 'date-fns';

// Components
import { Definition } from '../../../../../components/display/Definition';
import {
  Title as DefinitionLabel,
  Value as DefinitionValue,
} from '../../../../../components/display/Definition/styles';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';
// Pill de estado: el MISMO SpecialTag que pinta los estados en la tabla de
// work-orders/listado RV, vía el mapeo canónico `mapRouteStateToTagVariant`
// (identidad desde 2026-07-20: cada estado del RV tiene variante propia).
import { SpecialTag } from '../../../../../components/display/SpecialTag';
import { variant } from '../../../../../components/display/SpecialTag/types';

// Media (mismo criterio de tamaño responsive que usa `Definition` para sus
// labels `dt` — reutilizado aquí para las labels de las filas Operarios /
// Vehículos).
import { useIsLandscapeTablet } from '../../../../../hooks/useMediaBreakPoints';

// Hooks / RBAC
import { useAuth } from '../../../../../hooks/useAuth';
import {
  canEditSurveillanceRoute,
  canFinishRoute,
  canForceCloseRoute,
  canOperateTimer,
  formatPK,
  formatRoadsWithRanges,
  mapRouteStateToTagVariant,
  resolveRouteRoadRanges,
  type ValidateReviewWarning,
} from '../../../../../utils/surveillanceRoute';
import { Role } from '../../../../../types/auth';
import { getSrModalTransitions } from '../../../../../utils/rolePermissions';
import { OrderState, OrderType } from '../../../../../types/orders';
import {
  useOptimisticRouteState,
  OPTIMISTIC_CLOSING_STATES,
} from '../../../../../hooks/useOptimisticRouteState';
import { useRoadRangesById } from '../../../../../hooks/useRoadDirectionLabels';

// Redux — modal de cambio de estado (mismo SuperModal que genéricas/campañas)
import { openModal } from '../../../../../state/actions/ui/modal';
import { modalIds } from '../../../../../components/modals/SuperModal/types';
// Force-close del gestor (rv-recorrido-colgado 2026-07-22)
import { FORCE_CLOSE_SURVEILLANCE_ROUTE } from '../../../../../gql/mutations/surveillanceRoute';
import {
  showBackendErrorToast,
  showSuccessToast,
} from '../../../../../utils/toasts';

// Types
import {
  RouteState,
  SurveillanceRoute,
} from '../../../../../types/surveillanceRoute';
import { RouteHeaderData } from '../../types';

// Styles
import {
  ActionsRow,
  Chevron,
  Chip,
  ChipsRow,
  Container,
  ContentGrid,
  CrewRow,
  HeaderRight,
  HeaderRow,
  RoidText,
  RouteTitle,
} from './styles';

// Icons
import ArrowRight from '../../../../../assets/icons/triangle-right.svg';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface RouteHeaderCardProps {
  route: SurveillanceRoute;
  /** ROID de la Order del recorrido, ya resuelto por el loader. */
  roid: string;
  /** Nombres resueltos (concesión/carretera/equipo/operarios/vehículos). */
  headerData: RouteHeaderData;
  /** Handler existente de `RoutePreviewPage` — finaliza el recorrido. */
  onFinishRoute: () => void;
  /**
   * Arranca un RV `open` (review final C1) — el único caso que `TimerOverlay`
   * no cubre, porque `GetActiveSurveillanceRoutes` solo lista in-progress/
   * paused. Opcional: solo se renderiza el botón si se pasa el handler.
   */
  onStartRoute?: () => void;
  isLoading?: boolean;
  /**
   * «Tramos revisados» (D7, 27-oct): aviso que el modal de estado enseña antes
   * de confirmar «Validado» si quedan tramos sin revisar. Lo calcula la página
   * (es la que tiene la tabla y las marcas); `null` = no avisar.
   */
  segmentReviewWarning?: ValidateReviewWarning | null;
  /** «Revisar los que faltan» del aviso: lo mismo que «Siguiente sin revisar». */
  onReviewRemaining?: () => void;
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

/**
 * Cabecera del detalle del RV — patrón ShowOT (card colapsable, header row +
 * chevron + grid de `Definition` read-only). Reemplaza al panel lateral
 * `RouteControlPanel` (Task 3 lo elimina y monta esta card arriba del
 * contenido).
 *
 * NO renderiza el cronómetro en vivo (vive en el `TimerOverlay` de la
 * navbar) — solo datos estáticos + acciones de estado.
 */
export default function RouteHeaderCard({
  route,
  roid,
  headerData,
  onFinishRoute,
  onStartRoute,
  isLoading = false,
  segmentReviewWarning = null,
  onReviewRemaining,
}: RouteHeaderCardProps): JSX.Element {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const revalidator = useRevalidator();
  const { user } = useAuth();
  const currentUser = user?.user;
  const currentRole = currentUser?.rolActive as Role | undefined;
  const isLandscapeTablet = useIsLandscapeTablet();

  // Multi-carretera: cada rango pegado a SU carretera (ver
  // `formatRoadsWithRanges`). Con una sola, `roadsWithRanges` es el nombre
  // pelado y las filas de PK siguen debajo — o sea, la tarjeta de siempre.
  const isMultiRoad = (route.roadIDs?.length ?? 0) > 1;
  const roadRangesById = useRoadRangesById(route.roadIDs);
  const roadsWithRanges = useMemo(
    () =>
      formatRoadsWithRanges(
        route.roadIDs,
        headerData.roadNamesById ?? {},
        resolveRouteRoadRanges(route, roadRangesById)
      ) || headerData.roadName,
    [route, headerData.roadNamesById, headerData.roadName, roadRangesById]
  );

  const [showInfo, setShowInfo] = useState(true);
  const toggleInfo = useCallback(() => setShowInfo((prev) => !prev), []);

  // Estado OPTIMISTA offline (Opción B, 2026-07-29): si hay una transición de
  // estado ENCOLADA sin red para este RV (finish/start/validate/…), refleja YA
  // el estado que tendrá al sincronizar. Sin esto, tras "Finalizar" offline la
  // tarjeta seguía en "Trabajo en curso" con el botón "Finalizar" (snapshot del
  // loader), dejando re-disparar y re-encolar la acción (viste "8 pendientes").
  // Excluye GPS a propósito (ver el hook). Al drenar la cola vuelve a null.
  const optimisticRouteState = useOptimisticRouteState(route.id);
  const displayRoute = optimisticRouteState
    ? { ...route, state: optimisticRouteState }
    : route;
  // La pill solo deja de ser clicable cuando hay una transición TERMINAL
  // encolada (el RV se está cerrando) — así durante la patrulla offline (un
  // tramo/cambio de sentido encolado = optimista in-progress) la pill sigue
  // usable, y solo se bloquea el re-cambio si ya hay un finish/validate/annul
  // en cola (evita apilar dos transiciones). Feedback usuario 2026-07-29.
  const optimisticIsClosing =
    optimisticRouteState != null &&
    OPTIMISTIC_CLOSING_STATES.includes(optimisticRouteState);

  // ¿Puede ESTE usuario operar el timer de ESTE RV? (Tablet del equipo + dueño
  // de la patrulla viva / RV libre — ver `canOperateTimer`). Se calcula una vez
  // y se reusa para el botón Finalizar, Iniciar y el gate de la pill.
  const canOperateThisRoute = canOperateTimer(currentUser, displayRoute);

  // "Finalizar recorrido" — mismo criterio que tenía `RouteControlPanel`:
  // solo Tablet-miembros-del-equipo (`canOperateTimer`) y solo mientras el
  // recorrido está en curso o pausado (`canFinishRoute`). Sobre `displayRoute`
  // → tras finalizar offline, el botón desaparece aunque el snapshot siga viejo.
  const canFinish = canOperateThisRoute && canFinishRoute(displayRoute);

  // "Iniciar recorrido" — review final C1: mismo gate de propiedad
  // (`canOperateTimer`) que "Finalizar", pero solo mientras el RV sigue
  // `open` (aún no tiene ningún tramo). El resume-tras-pausa NO pasa por
  // aquí — lo cubre `TimerOverlay`.
  const canStart =
    Boolean(onStartRoute) &&
    canOperateThisRoute &&
    displayRoute.state === RouteState.OPEN;

  // "Editar" (F5 2026-07-17, spec §2.4) — gating rol×estado de la ENTIDAD SR
  // (nunca de la Order gemela): nunca en end/annulled, Tablet solo
  // open/in-progress, SuperAdmin/Coex/Capataz/RoomTeam en cualquier estado
  // no terminal. `returnTo` explícito al detalle — coincide con el fallback
  // de la action, pero lo hace autodescriptivo en vez de implícito.
  //
  // NOTA (2026-08-04): aquí vivió un botón "Informe" (añadido el 2026-07-27)
  // que abría el DownloadHistoricModal en modo `reportOnly`. Se retiró por
  // coherencia de producto: ningún detalle de entidad genera informes — ni el
  // de una OT genérica ni el de una campaña lo hacen. Los informes salen SOLO
  // de la sección Informes (`/reporting/*`), donde el RV ya tiene su propio
  // histórico (`search-create-historical-route`). No reintroducir aquí.
  // Quién y de quién (2026-09-23): una tablet de la otra patrulla ya no ve el
  // lápiz de un recorrido ajeno.
  const canEdit = canEditSurveillanceRoute(
    currentRole,
    displayRoute.state,
    currentUser,
    displayRoute
  );
  const handleEdit = useCallback(() => {
    navigate(
      `/surveillance-route/${route.id}/edit?returnTo=${encodeURIComponent(
        `/surveillance-route/${route.id}`
      )}`
    );
  }, [navigate, route.id]);

  // Pill de estado CLICABLE (2026-07-20, paridad genéricas/campañas): abre el
  // ChangeStateModal con la máquina D5 del RV. El submit del modal llama a
  // las mutations SR (validate/annul/reopen/finish) — nunca UPDATE_ORDER.
  // Gate: solo si la matriz D5 da alguna transición para este rol+estado
  // (RoomTeam y estados terminales quedan read-only).
  const srTransitions = currentRole
    ? getSrModalTransitions(currentRole, route.state as unknown as OrderState)
    : [];
  // Propiedad reclamable (2026-07-30): la pill es un 2º camino para cambiar el
  // estado (finalizar/reabrir…). Un Tablet solo la usa en un RV que PUEDE
  // operar — su patrulla viva, o un RV terminado que va a reabrir
  // (`canOperateTimer` devuelve true fuera de in-progress/paused). Sin esto, un
  // Tablet podía FINALIZAR por la pill un RV pausado AJENO (la patrulla viva de
  // otro), justo lo que el botón "Finalizar" ya bloquea. Los gestores
  // (Coex/Capataz/SuperAdmin) NO son Tablet → sin restricción de propiedad
  // (validan/anulan/reabren por revisión, como hasta ahora).
  const canChangeSrState =
    srTransitions.length > 0 &&
    (currentRole !== Role.Tablet || canOperateThisRoute);
  const openChangeStateModal = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!canChangeSrState) return;
      dispatch(
        openModal(modalIds.CHANGE_STATE_MODAL, {
          // Int en el schema: nada de String() aquí (rompía FetchOrders y con
          // ella el aviso de cierre del modal).
          indexExpedition: route.indexExpedition,
          revalidatorFunction: () => revalidator.revalidate(),
          orderType: OrderType.SurveillanceRoute,
          currentState: route.state,
          orderId: route.orderIDs?.[0] ?? '',
          routeId: route.id,
          // Para el modal de cierre de tramo si la entrega lo requiere.
          roadId: route.roadIDs?.[0],
          // D7: el modal solo lo usa al elegir «Validado».
          segmentReviewWarning,
          onReviewRemaining,
        })
      );
    },
    [
      canChangeSrState,
      dispatch,
      revalidator,
      route,
      segmentReviewWarning,
      onReviewRemaining,
    ]
  );

  // Force-close del gestor (rv-recorrido-colgado 2026-07-22): botón visible a
  // Coex/Capataz/SuperAdmin SOLO si el RV está COLGADO (>24h sin actividad).
  // El backend es la autoridad (rechaza SR_ROUTE_NOT_STALE si ya no lo está).
  const canForceClose = canForceCloseRoute(currentRole, route);
  const [forceCloseRoute] = useMutation(FORCE_CLOSE_SURVEILLANCE_ROUTE);
  // Pre-relleno de la hora de fin: el último tramo cerrado, o el registerInit.
  const forceCloseDefaultEnd = useMemo(() => {
    const ends = (route.segments ?? [])
      .map((s) => s.endDate)
      .filter((d): d is string => Boolean(d))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return ends[0] ?? route.registerInitDate ?? undefined;
  }, [route.segments, route.registerInitDate]);
  const handleForceClose = useCallback(() => {
    dispatch(
      openModal(modalIds.FORCE_CLOSE_ROUTE, {
        roid,
        defaultEndDate: forceCloseDefaultEnd,
        onConfirm: async (registerEndDate: string) => {
          try {
            await forceCloseRoute({
              variables: { input: { id: route.id, registerEndDate } },
            });
            showSuccessToast(t('toasts:success.updated'));
            revalidator.revalidate();
          } catch (err) {
            showBackendErrorToast(
              err,
              t('modals:changeState.errors.generic')
            );
            throw err;
          }
        },
      })
    );
  }, [
    dispatch,
    roid,
    forceCloseDefaultEnd,
    forceCloseRoute,
    route.id,
    t,
    revalidator,
  ]);

  // Aviso al operario (T8, best-effort): si el gestor forzó el cierre de ESTE
  // recorrido y quien lo mira es un operario del RV (no el propio gestor),
  // toast una vez por sesión (sessionStorage guard). Sin sistema de
  // notificaciones persistente — es best-effort si el operario entra.
  useEffect(() => {
    const closerId = route.forceClosedById;
    if (!closerId || !currentUser?.id) return;
    const isOperator = (route.userIDs ?? []).includes(currentUser.id);
    if (!isOperator || currentUser.id === closerId) return;
    const key = `sr-force-close-seen-${route.id}-${route.forceClosedAt ?? ''}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    showSuccessToast(t('pages:surveillance.forceClose.operatorNotice', { roid }));
  }, [
    route.forceClosedById,
    route.forceClosedAt,
    route.userIDs,
    route.id,
    currentUser?.id,
    roid,
    t,
  ]);

  // "Fecha inicio" real de la patrulla = arranque del PRIMER tramo (2026-07-31,
  // feedback usuario). Antes se mostraba `registerInitDate`, pero eso es la
  // fecha de CREACIÓN del RV (ya no se sobrescribe al arrancar — ver
  // `start()` backend) → coincidía con la fila "Fecha de creación" y confundía.
  // Sin tramos (RV `open`) → "Sin iniciar". La hora de fin es simétrica
  // (`finalEndDate`, último tramo cerrado).
  const actualStartDate = useMemo(() => {
    const starts = (route.segments ?? [])
      .map((segment) => segment.startDate)
      .filter((date): date is string => Boolean(date))
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    return starts[0] ?? null;
  }, [route.segments]);

  // Fecha/hora de finalización real: solo en estados terminales de
  // validación (end/pending-validation) y solo si hay algún tramo cerrado —
  // se usa el `endDate` más reciente entre todos los segments.
  const finalEndDate = useMemo(() => {
    const isFinalState =
      route.state === RouteState.END ||
      route.state === RouteState.PENDING_VALIDATION;
    if (!isFinalState) return null;

    const closedEndDates = (route.segments ?? [])
      .map((segment) => segment.endDate)
      .filter((date): date is string => Boolean(date));
    if (closedEndDates.length === 0) return null;

    return closedEndDates.reduce((latest, current) =>
      new Date(current).getTime() > new Date(latest).getTime()
        ? current
        : latest
    );
  }, [route.state, route.segments]);

  // "En manos de" (visibilidad de propiedad, 2026-07-30): quién tiene el timer
  // AHORA. En estados terminales (end/annulled) no aplica (null → no se pinta).
  // Con dueño → su nombre; `open` → "Sin iniciar"; resto sin dueño (pausado
  // liberado) → "Libre". Usa `displayRoute` para reflejar el optimista offline.
  const timerHolderValue: string | null = (() => {
    if (
      displayRoute.state === RouteState.END ||
      displayRoute.state === RouteState.ANNULLED
    ) {
      return null;
    }
    if (headerData.ownerName) return headerData.ownerName;
    if (displayRoute.state === RouteState.OPEN) {
      return t('pages:surveillance.timerNotStarted');
    }
    return t('pages:surveillance.timerFree');
  })();

  return (
    <Container>
      <HeaderRow onClick={toggleInfo}>
        <RouteTitle>
          {route.name || t('pages:surveillance.unnamedRoute')}
        </RouteTitle>
        <HeaderRight>
          <RoidText size="S">{roid}</RoidText>
          {/*
            2026-07-20: pill CLICABLE (cierra el follow-up C2/I1) — abre
            ChangeStateModal con orderType SurveillanceRoute; el modal enruta
            el submit a las mutations SR (validate/annul/reopen/finish) que
            validan matriz D5 en backend y cascadean a tramos. Para roles o
            estados sin transiciones (RoomTeam, end/annulled) sigue read-only.
          */}
          <SpecialTag
            variant={
              mapRouteStateToTagVariant(String(displayRoute.state)) as variant
            }
            sizeTag={isLandscapeTablet ? 'small' : 'default'}
            onClick={
              canChangeSrState && !optimisticIsClosing
                ? openChangeStateModal
                : undefined
            }
            style={
              canChangeSrState && !optimisticIsClosing
                ? { cursor: 'pointer' }
                : undefined
            }
          />
          <Chevron
            src={ArrowRight}
            alt={t('common:expandInfo')}
            $showInfo={showInfo}
          />
        </HeaderRight>
      </HeaderRow>

      {showInfo && (
        <>
          <ContentGrid>
            <Definition
              title={t('pages:surveillance.startDate')}
              value={
                actualStartDate
                  ? format(new Date(actualStartDate), 'dd/MM/yyyy HH:mm')
                  : t('pages:surveillance.timerNotStarted')
              }
            />
            {finalEndDate && (
              <Definition
                title={t('pages:surveillance.endDate')}
                value={format(new Date(finalEndDate), 'dd/MM/yyyy HH:mm')}
              />
            )}
            <Definition
              title={t('pages:surveillance.concession')}
              value={headerData.concessionName}
            />
            {/* Multi-carretera (2026-08-17): con VARIAS, el rango va pegado a
                su carretera y las filas sueltas de PK desaparecen. El par
                `pkInit`/`pkEnd` escalar apunta a la PRIMERA, así que enseñarlo
                bajo un título que habla del recorrido entero era mentir: los PK
                de una troncal y su ramal no son comparables, y aquí decía
                «82+800 → 235+800» en un recorrido que también cubre el
                234+000 → 237+100 del ramal.
                Con UNA sola carretera esto es exactamente lo de siempre. */}
            <Definition
              title={t('pages:surveillance.road')}
              value={roadsWithRanges}
            />
            {!isMultiRoad && (
              <>
                <Definition
                  title={t('pages:surveillance.pkInit')}
                  value={formatPK(route.pkInitKm, route.pkInitMeter)}
                />
                <Definition
                  title={t('pages:surveillance.pkEnd')}
                  value={formatPK(route.pkEndKm, route.pkEndMeter)}
                />
              </>
            )}
            {/* "Equipo" no tiene key propia verificada en pages:surveillance —
                reuso `forms:workOrder.team` ("Equipo" exacto en ca/es/en, ya
                usado por el formulario canónico de WorkOrder). */}
            <Definition
              title={t('forms:workOrder.team')}
              value={headerData.teamName}
            />
            {route.creationDate && (
              <Definition
                title={t('common:creationDate')}
                value={format(new Date(route.creationDate), 'dd/MM/yyyy HH:mm')}
              />
            )}
            {/* Visibilidad de propiedad (2026-07-30): quién la CREÓ y quién
                tiene el TIMER ahora — para que el operario entienda de un
                vistazo por qué puede/no puede operarla. */}
            {headerData.creatorName && (
              <Definition
                title={t('pages:surveillance.createdBy')}
                value={headerData.creatorName}
              />
            )}
            {timerHolderValue && (
              <Definition
                title={t('pages:surveillance.timerHolder')}
                value={timerHolderValue}
              />
            )}
          </ContentGrid>

          {headerData.operators.length > 0 && (
            <CrewRow>
              <DefinitionLabel
                size={isLandscapeTablet ? 'XS' : 'S'}
                component="dt"
              >
                {/* `operators` ("Operarios") y no `operatorsTitle` ("Selección
                    de operarios y vehículos") — esta es una fila de LECTURA,
                    no el título del form de selección; simétrica con la fila
                    Vehículos (forms:workOrder.vehicles). */}
                {t('forms:workOrder.operators')}:
              </DefinitionLabel>
              <ChipsRow>
                {headerData.operators.map((operator) => (
                  <Chip key={operator.id}>{operator.fullName}</Chip>
                ))}
              </ChipsRow>
            </CrewRow>
          )}

          {headerData.vehicles.length > 0 && (
            // Fila propia espejo de Operarios (review Task 2). Label:
            // `forms:workOrder.vehicles` ("Vehículos" exacto) en vez de
            // `common:tabs.operatorsAndVehicles` (que traduce a "Operarios",
            // confuso para esta fila). Entradas: texto `plate (driverName)`
            // con el mismo styled `Value` de `Definition` (no chips — el
            // brief pide texto aquí).
            <CrewRow>
              <DefinitionLabel
                size={isLandscapeTablet ? 'XS' : 'S'}
                component="dt"
              >
                {t('forms:workOrder.vehicles')}:
              </DefinitionLabel>
              <ChipsRow>
                {headerData.vehicles.map((vehicle, index) => (
                  <DefinitionValue
                    key={`${vehicle.plate}-${index}`}
                    size={isLandscapeTablet ? 'XS' : 'S'}
                    variant="semiBold"
                    component="dd"
                  >
                    {vehicle.driverName
                      ? `${vehicle.plate} (${vehicle.driverName})`
                      : vehicle.plate}
                  </DefinitionValue>
                ))}
              </ChipsRow>
            </CrewRow>
          )}

          {/*
            "Iniciar" (open) y "Finalizar" (in-progress/paused) son
            mutuamente excluyentes por estado — pueden compartir la fila sin
            colisionar nunca (review final C1). "Editar" (F5) puede coexistir
            con cualquiera de los dos (mismos estados no-terminales).
          */}
          {(canStart || canFinish || canEdit || canForceClose) && (
            <ActionsRow>
              {canStart && (
                <MainButton
                  text={t('pages:surveillance.startRoute')}
                  onClick={onStartRoute}
                  disabled={isLoading}
                />
              )}
              {canFinish && (
                <MainButton
                  text={t('pages:surveillance.finishRoute')}
                  onClick={onFinishRoute}
                  disabled={isLoading}
                />
              )}
              {canEdit && (
                <MainButton
                  variant="secondary"
                  text={t('common:buttons.edit')}
                  onClick={handleEdit}
                  disabled={isLoading}
                />
              )}
              {/* Cerrar recorrido colgado (gestor) — solo si el RV es stale */}
              {canForceClose && (
                <MainButton
                  variant="secondary"
                  text={t('pages:surveillance.forceClose.button')}
                  onClick={handleForceClose}
                  disabled={isLoading}
                  data-testid="force-close-route-btn"
                />
              )}
            </ActionsRow>
          )}
        </>
      )}
    </Container>
  );
}
